import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import TokenExpirationModal from "../components/common/TokenExpirationModal";
import { clearAuthData, setTokenExpirationHandler } from "../services/api";
import { useAuth } from "./AuthContext";

const TokenExpirationContext = createContext();

export const useTokenExpiration = () => {
  const context = useContext(TokenExpirationContext);
  if (!context) {
    throw new Error(
      "useTokenExpiration must be used within a TokenExpirationProvider"
    );
  }
  return context;
};

export const TokenExpirationProvider = ({ children }) => {
  const [showExpirationModal, setShowExpirationModal] = useState(false);
  const navigate = useNavigate();
  const { forceLogout } = useAuth();

  const handleTokenExpiration = () => {
    // Prevent multiple modals from showing
    if (!showExpirationModal) {
      setShowExpirationModal(true);
    }
  };

  // Register the token expiration handler with the API service
  useEffect(() => {
    setTokenExpirationHandler(handleTokenExpiration);

    // Cleanup function to remove the handler when component unmounts
    return () => {
      setTokenExpirationHandler(null);
    };
  }, []);

  const handleModalConfirm = () => {
    // Clear all auth data and update auth context
    clearAuthData();
    forceLogout();

    // Navigate to login page
    navigate("/login", { replace: true });

    // Close modal
    setShowExpirationModal(false);
  };

  const handleModalClose = () => {
    // Still redirect to login even if user closes modal
    clearAuthData();
    forceLogout();
    navigate("/login", { replace: true });
    setShowExpirationModal(false);
  };

  const value = {
    handleTokenExpiration,
  };

  return (
    <TokenExpirationContext.Provider value={value}>
      {children}
      <TokenExpirationModal
        isOpen={showExpirationModal}
        onClose={handleModalClose}
        onConfirm={handleModalConfirm}
        title="Session Expired"
        message="Your session has expired. Please log in again to continue."
        confirmText="OK"
      />
    </TokenExpirationContext.Provider>
  );
};
